import { z } from 'zod';
import { DomainError } from '../errors.js';
import { toIssues } from '../materials/catalog.js';
import type { ParseResult } from '../voyage/schemas.js';

/**
 * Mal kabulü (GRN; ADR 0026 §1, ADR 0040 #183–#187; `satin-alma-modulu.md` §9, §10;
 * `satin-alma-akisi.jpeg` "Delivery & GRN"). Gemide, internetsiz, sipariş satırına karşı girilir;
 * kabul edilen miktar aynı transaction'da stoğa girer. Hata iletileri koddur; arayüz çevirir.
 */

const missing = (input: unknown) => input === undefined || input === null;
const typed = (code: string) => ({
  error: (issue: { input?: unknown }) => (missing(issue.input) ? 'REQUIRED' : code),
});
const optionalText = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform((value) => value.normalize('NFC').trim())
    .pipe(z.string().max(max, 'TOO_LONG'))
    .transform((value) => (value === '' ? null : value))
    .nullish()
    .transform((value) => value ?? null);
const MAX_QTY = 1_000_000_000;
const quantity = () =>
  z.number(typed('INVALID_NUMBER')).min(0, 'OUT_OF_RANGE').max(MAX_QTY, 'OUT_OF_RANGE');

/** Kalite kontrol sonucu (`satin-alma-modulu.md` §10). */
export const GRN_CONDITIONS = [
  'accepted',
  'accepted_with_remarks',
  'rejected',
  'damaged',
  'wrong_item',
  'short_delivery',
  'certificate_missing',
  'specification_not_met',
] as const;
export type GrnCondition = (typeof GRN_CONDITIONS)[number];

/** Kabul edilmiş sayılan sonuçlar: hasarlı miktar taşımaz. */
const ACCEPTED: ReadonlySet<GrnCondition> = new Set(['accepted', 'accepted_with_remarks']);
/** Hiçbir şeyin kabul edilmediği sonuçlar. */
const NOTHING_ACCEPTED: ReadonlySet<GrnCondition> = new Set(['rejected', 'wrong_item']);

/** En çok satır: bir PO'nun kalemleri (`MAX_REQUISITION_LINES`). */
export const MAX_GRN_LINES = 200;

export const goodsReceiptLineSchema = z.object({
  purchaseOrderLineId: z.uuid(typed('INVALID_ID')),
  /** Kabul edilen: stoğa giren (tasarımda "Received"). */
  acceptedQty: quantity(),
  /** Teslim edildi ama hasarlı: stoğa girmez, açık kalır. */
  damagedQty: quantity().default(0),
  /** Bu teslimatta gelmesi beklenip gelmeyen: açık kalır. */
  missingQty: quantity().default(0),
  condition: z.enum(GRN_CONDITIONS, typed('INVALID_OPTION')).default('accepted'),
  serialNo: optionalText(80),
  batchNo: optionalText(80),
  expiryDate: z.iso
    .date(typed('INVALID_DATE'))
    .nullish()
    .transform((value) => value ?? null),
  remarks: optionalText(500),
});
export type GoodsReceiptLine = z.output<typeof goodsReceiptLineSchema>;

/**
 * Kalite sonucu miktarlarla tutarlı olmalı (ADR 0040 #184; `CONDITION_MISMATCH`): kabul edilen
 * satırda hasarlı miktar yok, reddedilen ve yanlış kalemde kabul yok, hasarlıda hasarlı miktar,
 * eksik teslimde eksik miktar var.
 */
type ConditionInput = Pick<
  GoodsReceiptLine,
  'condition' | 'acceptedQty' | 'damagedQty' | 'missingQty'
>;

function conditionMismatch(line: ConditionInput): boolean {
  if (ACCEPTED.has(line.condition)) return line.damagedQty > 0;
  if (NOTHING_ACCEPTED.has(line.condition)) return line.acceptedQty > 0;
  if (line.condition === 'damaged') return line.damagedQty <= 0;
  if (line.condition === 'short_delivery') return line.missingQty <= 0;
  return false;
}

/** Kalite sonucu miktarlarla tutarlı mı; değilse `CONDITION_MISMATCH` (kayıt katmanı da sorar). */
export function assertGrnLineCondition(line: ConditionInput): void {
  if (!(GRN_CONDITIONS as readonly string[]).includes(line.condition)) {
    throw new DomainError('INVALID_GRN_LINE', `condition:${String(line.condition)}`);
  }
  if (conditionMismatch(line)) throw new DomainError('CONDITION_MISMATCH', line.condition);
}

/** GRN başlığı ve satırları. GRN numarası ve gemi kayıt katmanında gemi sunucusundan yazılır. */
export const goodsReceiptSchema = z
  .object({
    purchaseOrderId: z.uuid(typed('INVALID_ID')),
    /** Teslim günü: gemi yerel günü, gelecek olamaz (kayıt katmanı denetler). */
    receivedDate: z.iso.date(typed('INVALID_DATE')),
    /** Teslim irsaliyesi (Delivery Note). */
    deliveryNote: optionalText(80),
    /** Teslim alan (Received By): o gün gemide görevli kişi. */
    receivedBy: z.uuid(typed('INVALID_ID')),
    remarks: optionalText(1000),
    lines: z
      .array(goodsReceiptLineSchema, typed('INVALID_LINES'))
      .min(1, 'NO_LINES')
      .max(MAX_GRN_LINES, 'TOO_MANY_LINES'),
  })
  .superRefine((receipt, ctx) => {
    const seen = new Set<string>();
    for (const [index, line] of receipt.lines.entries()) {
      if (seen.has(line.purchaseOrderLineId)) {
        ctx.addIssue({
          code: 'custom',
          message: 'DUPLICATE_LINE',
          path: ['lines', index, 'purchaseOrderLineId'],
        });
      }
      seen.add(line.purchaseOrderLineId);
      if (conditionMismatch(line)) {
        ctx.addIssue({
          code: 'custom',
          message: 'CONDITION_MISMATCH',
          path: ['lines', index, 'condition'],
        });
      }
    }
  });
export type GoodsReceipt = z.output<typeof goodsReceiptSchema>;

export function parseGoodsReceipt(input: unknown): ParseResult<GoodsReceipt> {
  const result = goodsReceiptSchema.safeParse(input);
  return result.success
    ? { ok: true, value: result.data }
    : { ok: false, issues: toIssues(result.error) };
}

/**
 * Sıradaki GRN numarası (ADR 0040 #183): gemi başına yıl içinde sıralı `GRN-YYYY-NNN` (tasarımda
 * "GRN-2026-042"). Gemi internetsiz numara verir; tekillik gemi ve numara çiftindedir.
 */
export function nextGoodsReceiptNo(existing: readonly string[], year: number): string {
  let last = 0;
  for (const no of existing) {
    const match = /^GRN-(\d{4})-(\d+)$/.exec(no);
    if (match && Number(match[1]) === year) last = Math.max(last, Number(match[2]));
  }
  return `GRN-${year}-${String(last + 1).padStart(3, '0')}`;
}

const PRECISION = 1e6;
const round = (value: number) => Math.round(value * PRECISION) / PRECISION;

export interface ReceiptQuantities {
  purchaseOrderLineId: string;
  acceptedQty: number;
  damagedQty: number;
  missingQty: number;
}

/**
 * GRN satırlarının açık miktara göre denetimi (ADR 0040 #184):
 * - her açık kalem satırda olmalı, sıfır miktarla da olsa (`RECEIPT_LINES_INCOMPLETE`): GRN o
 *   teslimatın tam kaydıdır, durumu satırlarından türer;
 * - satır PO'nun kalemi olmalı (`UNKNOWN_PO_LINE`);
 * - kabul + hasarlı + eksik açık miktarı aşamaz (`OVER_RECEIPT`; fazla teslim yok);
 * - en az bir miktar sıfırdan büyük (`EMPTY_RECEIPT`).
 */
export function checkReceiptLines(
  open: readonly { id: string; open: number }[],
  lines: readonly ReceiptQuantities[],
): void {
  const byId = new Map(open.map((line) => [line.id, round(line.open)]));
  const given = new Set(lines.map((line) => line.purchaseOrderLineId));
  for (const line of lines) {
    const available = byId.get(line.purchaseOrderLineId);
    if (available === undefined) {
      throw new DomainError('UNKNOWN_PO_LINE', line.purchaseOrderLineId);
    }
    const total = round(line.acceptedQty + line.damagedQty + line.missingQty);
    if (total > available) {
      throw new DomainError('OVER_RECEIPT', `${line.purchaseOrderLineId}: ${total} > ${available}`);
    }
  }
  for (const line of open) {
    if (round(line.open) > 0 && !given.has(line.id)) {
      throw new DomainError('RECEIPT_LINES_INCOMPLETE', line.id);
    }
  }
  if (lines.every((line) => round(line.acceptedQty + line.damagedQty + line.missingQty) <= 0)) {
    throw new DomainError('EMPTY_RECEIPT');
  }
}

export type GrnLineStatus = 'ok' | 'partial' | 'issue';

/**
 * Satırın durumu (tasarımda "OK"): açık miktarın tamamı kabul edildi ve sonuç kabul; hasarlı ya
 * da kabul dışı sonuç sorunlu; kalanı kısmi. Açık miktar satırda saklanır (`open_qty`).
 */
export function grnLineStatus(line: {
  openQty: number;
  acceptedQty: number;
  damagedQty: number;
  missingQty: number;
  condition: GrnCondition;
}): GrnLineStatus {
  if (line.damagedQty > 0 || !(ACCEPTED.has(line.condition) || line.condition === 'short_delivery'))
    return 'issue';
  return round(line.acceptedQty) >= round(line.openQty) && line.missingQty <= 0 ? 'ok' : 'partial';
}

export type GoodsReceiptStatus = 'completed' | 'partial' | 'cancelled';

/** GRN durumu: iptal; bütün satırlar OK ise tamamlandı (tasarımda "Completed"); değilse kısmi. */
export function goodsReceiptStatus(
  lines: readonly GrnLineStatus[],
  cancelled: boolean,
): GoodsReceiptStatus {
  if (cancelled) return 'cancelled';
  return lines.every((status) => status === 'ok') ? 'completed' : 'partial';
}
